<script lang="ts" setup>
import { ref } from "vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import type { Config } from "datatables.net";

const timeRange = ref("30d");

const overallMetrics = ref({
  totalRevenue: 28470,
  totalSubscribers: 127,
  avgSuccessRate: 98.2,
  totalRuns: 4521,
});

const productMetrics = ref([
  {
    id: "1",
    name: "Email Automation Suite",
    subscribers: 47,
    revenue: 12400,
    successRate: 99.1,
    runs: 1247,
    trend: "+15%",
  },
  {
    id: "2",
    name: "CRM Integration",
    subscribers: 32,
    revenue: 8900,
    successRate: 97.3,
    runs: 856,
    trend: "+8%",
  },
  {
    id: "3",
    name: "Analytics Suite",
    subscribers: 28,
    revenue: 5200,
    successRate: 98.8,
    runs: 623,
    trend: "+22%",
  },
  {
    id: "4",
    name: "Data Processor",
    subscribers: 20,
    revenue: 1970,
    successRate: 96.5,
    runs: 421,
    trend: "+5%",
  },
]);

const topErrors = ref([
  {
    id: "1",
    message: "Email service timeout",
    count: 12,
    product: "Email Automation Suite",
    percentage: 45.5,
  },
  {
    id: "2",
    message: "CRM connection failed",
    count: 8,
    product: "CRM Integration",
    percentage: 30.3,
  },
  {
    id: "3",
    message: "Invalid email format",
    count: 4,
    product: "Email Automation Suite",
    percentage: 15.2,
  },
  {
    id: "4",
    message: "Rate limit exceeded",
    count: 3,
    product: "Analytics Suite",
    percentage: 11.4,
  },
]);

const revenueHistory = ref([
  { month: "Jan", revenue: 18200 },
  { month: "Feb", revenue: 21400 },
  { month: "Mar", revenue: 19800 },
  { month: "Apr", revenue: 23600 },
  { month: "May", revenue: 26200 },
  { month: "Jun", revenue: 28470 },
]);

const tableOptions = {
  columns: [
    { data: 'id', title: 'ID', visible: false },
    { data: 'name', title: 'Product', render: (d: string) => `<div class="fw-medium">${d}</div>` },
    { data: 'subscribers', title: 'Subscribers' },
    { data: 'revenue', title: 'Revenue', render: (d: number) => `<span class="fw-medium">$${d.toLocaleString()}</span>` },
    { data: 'successRate', title: 'Success Rate', render: (d: number) => `<span class="badge ${d >= 98 ? 'bg-success' : d >= 95 ? 'bg-warning' : 'bg-danger'}">${d}%</span>` },
    { data: 'runs', title: 'Runs', render: (d: number) => d.toLocaleString() },
    { data: 'trend', title: 'Trend', render: (d: string) => `<span class="${getTrendClass(d)}"><i class="bi bi-arrow-${d.startsWith('+') ? 'up' : 'down'}"></i> ${d}</span>` },
  ],
  layout: {
    topEnd: '',
    bottomEnd: '',
    bottomStart: '',
  },
} as Config;

function exportAnalytics() {}

function getTrendClass(trend: string): string {
  return trend.startsWith("+") ? "text-success" : "text-danger";
}
</script>

<template>
  <div>
    <PageHeader
      title="Analytics Dashboard"
      :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
        { label: 'Publisher', href: '/publisher' },
        { label: 'Analytics' }
      ]"
    >
      <template #actions>
        <select class="form-select w-auto" v-model="timeRange">
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
        <button class="bb-btn-secondary-cta text-nowrap" @click="exportAnalytics">
          <i class="bi bi-download me-2"></i>Export
        </button>
      </template>
    </PageHeader>

    <!-- Coming Soon Banner — analytics content below is demo data with no real backing yet.
         Layout/intent stays visible; everything's greyed out + pointer-disabled. -->
    <div class="alert alert-info d-flex align-items-center mb-4" role="alert">
      <i class="bi bi-info-circle me-2"></i>
      <span>Analytics are coming soon. The data shown below is illustrative only.</span>
    </div>

    <div class="coming-soon">
    <!-- Overall Metrics -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-success mb-1">
              ${{ overallMetrics.totalRevenue.toLocaleString() }}
            </div>
            <small class="text-muted">Total Revenue</small>
            <div class="text-success small mt-1">+18% vs last period</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-primary mb-1">
              {{ overallMetrics.totalSubscribers }}
            </div>
            <small class="text-muted">Total Subscribers</small>
            <div class="text-success small mt-1">+12 this month</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-info mb-1">{{ overallMetrics.avgSuccessRate }}%</div>
            <small class="text-muted">Avg Success Rate</small>
            <div class="text-success small mt-1">+0.3% improvement</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-warning mb-1">
              {{ overallMetrics.totalRuns.toLocaleString() }}
            </div>
            <small class="text-muted">Total Runs</small>
            <div class="text-success small mt-1">+25% vs last month</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <!-- Product Performance -->
      <div class="col-lg-8">
        <!-- Product Performance -->
        <div class="card mb-4">
          <div class="card-body">
            <h2 class="card-title mb-3">Product Performance</h2>
            <DataView
              :data="productMetrics"
              view-mode="table"
              :options="tableOptions"
            >
          <template #grid-card="{ row: product }">
            <div class="card h-100 card-hover">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="mb-0 fw-medium">{{ product.name }}</h6>
                  <span class="badge" :class="product.successRate >= 98 ? 'bg-success' : product.successRate >= 95 ? 'bg-warning' : 'bg-danger'">
                    {{ product.successRate }}%
                  </span>
                </div>
                <div class="small text-muted mb-3">
                  <div><i class="bi bi-people me-1"></i>{{ product.subscribers }} subscribers</div>
                  <div><i class="bi bi-currency-dollar me-1"></i>${{ product.revenue.toLocaleString() }} revenue</div>
                  <div><i class="bi bi-play-circle me-1"></i>{{ product.runs.toLocaleString() }} runs</div>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted small">Performance</span>
                  <span :class="getTrendClass(product.trend)">
                    <i :class="product.trend.startsWith('+') ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                    {{ product.trend }}
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template #empty-state>
            <div class="text-center py-5">
              <i class="bi bi-box-seam display-1 text-muted"></i>
              <h3 class="mt-3 text-muted">No product data available</h3>
            </div>
          </template>
        </DataView>
          </div>
        </div>

        <!-- Revenue Trend (desktop only) -->
        <div class="card d-none d-md-block">
          <div class="card-body">
            <h2 class="card-title mb-3">Revenue Trend</h2>
            <div class="bg-light-subtle rounded p-3">
              <div
                class="d-flex justify-content-between align-items-end"
                style="height: 200px; padding: 20px"
              >
                <div
                  v-for="(data, index) in revenueHistory"
                  :key="index"
                  class="d-flex flex-column align-items-center justify-content-end flex-fill mx-1"
                  :style="{ height: (data.revenue / 30000) * 100 + '%' }"
                >
                  <div
                    class="bg-primary mb-2 rounded-top"
                    style="width: 20px"
                    :style="{ height: '100%' }"
                  ></div>
                  <div class="text-muted small mt-2">{{ data.month }}</div>
                  <div class="text-muted small fw-medium">
                    ${{ (data.revenue / 1000).toFixed(0) }}k
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Reports & Quick Stats -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <!-- Top Errors Section -->
            <h2 class="card-title mb-3">Top Errors</h2>
            <div class="d-flex flex-column gap-3 mb-4">
              <div
                v-for="error in topErrors"
                :key="error.id"
                class="pb-3 border-bottom"
              >
                <div
                  class="d-flex justify-content-between align-items-start mb-1"
                >
                  <span class="fw-medium">{{ error.message }}</span>
                  <span class="badge bg-danger">{{ error.count }}</span>
                </div>
                <div class="text-muted small mb-1">{{ error.product }}</div>
                <div class="text-muted small">
                  {{ error.percentage }}% of total errors
                </div>
              </div>
            </div>

            <!-- Quick Reports Section -->
            <hr class="my-4" />
            <h2 class="card-title mb-3">Quick Reports</h2>
            <div class="d-flex flex-column gap-2">
              <button class="bb-btn-secondary-cta w-100 text-start">
                <i class="bi bi-file-earmark-bar-graph"></i> Revenue Report
              </button>
              <button class="bb-btn-secondary-cta w-100 text-start">
                <i class="bi bi-people"></i> Subscriber Report
              </button>
              <button class="bb-btn-secondary-cta w-100 text-start">
                <i class="bi bi-graph-up"></i> Performance Report
              </button>
              <button class="bb-btn-secondary-cta w-100 text-start">
                <i class="bi bi-exclamation-triangle"></i> Error Report
              </button>
              <hr class="my-2" />
              <button class="bb-btn-secondary-cta w-100 text-start">
                <i class="bi bi-gear"></i> Configure Alerts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* COMMENTED OUT: Redundant - Bootstrap already handles responsive grid behavior */
/* Force column widths to ensure proper layout - 2 per row on mobile */
/*@media (max-width: 767.98px) {*/
/*  .row .col-6 {*/
/*    flex: 0 0 50%;*/
/*    max-width: 50%;*/
/*  }*/
/*}*/

/*@media (min-width: 768px) {*/
/*  .row .col-md-3 {*/
/*    flex: 0 0 25%;*/
/*    max-width: 25%;*/
/*  }*/
/*}*/

</style>
